import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import { Box, Card, Checkbox, Typography } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { clickableProps } from '../../helpers/interactive'
import { relativePct } from '../../helpers/relativeMetric'
import { focusRingSx, gameIconSx, linkTypographyBoldSx } from '../../helpers/styles'
import { useUAInsightsActions, useUAInsightsScope } from '../../hooks/useUAInsightsNavigation'
import { CreativeAd } from '../../types'
import { CreativeAdTooltipContent } from '../CreativeAdTooltipContent/CreativeAdTooltipContent'
import { RelativeMetricBar } from '../RelativeMetricBar/RelativeMetricBar'

interface CreativeAdCardGridProps {
  rows: CreativeAd[]
  /** Pre-computed max share-of-voice across the visible rows; used to size the relative performance bar. */
  maxInstallShare: number
  /**
   * When provided the cards expose a compare checkbox (top-left of the
   * thumbnail). Mirrors the table-mode compare workflow so the user can
   * switch between views without losing selection state.
   */
  selectedAdIds?: Set<string>
  onToggleSelection?: (adId: string) => void
}

/**
 * Card-grid alternative to the `CreativeAdsTable`. Each card surfaces the
 * same fields as the table row (thumbnail, game, network, duration,
 * performance) in a more visual layout — the thumbnail is the primary
 * affordance and the text rows act as supporting metadata.
 *
 * Sorting is owned by the parent (`CreativeAdsTable`) so the same `rows`
 * array drives both views and switching modes preserves the active sort.
 */
export const CreativeAdCardGrid: FC<CreativeAdCardGridProps> = ({ rows, maxInstallShare, selectedAdIds, onToggleSelection }) => {
  const { t } = useTranslation('ua-insights')
  const { openAd, openGame } = useUAInsightsActions()
  const { month: currentMonth } = useUAInsightsScope()

  if (rows.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('no_ads')}
        </Typography>
      </Box>
    )
  }

  const compareEnabled = Boolean(onToggleSelection)
  const selectionFull = compareEnabled && (selectedAdIds?.size ?? 0) >= 2

  return (
    <Box
      sx={{
        display: 'grid',
        // `auto-fill` + `minmax` lets the grid pack as many cards as fit in
        // the container width without any breakpoint juggling, collapsing
        // gracefully to 1–2 columns on narrow viewports. The 180px minimum
        // is tuned so a typical desktop viewport fits 5 cards per row
        // while still keeping each thumbnail readable.
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 2,
        p: 2,
      }}
    >
      {rows.map((row) => {
        const performancePct = relativePct(row.installShare ?? 0, maxInstallShare)
        const isSelected = selectedAdIds?.has(row.id) ?? false
        const checkboxDisabled = compareEnabled && !isSelected && selectionFull

        return (
          <Card
            key={row.id}
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderColor: 'rgba(0, 0, 0, 0.06)',
              // Soft ambient shadow gives each card a subtle lift off the
              // background so the grid reads as a set of distinct objects
              // rather than a flat tile sheet. Combined with the hover state
              // below, the card grows a touch and brightens its shadow,
              // reinforcing that the whole card is interactive.
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.06)',
              transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 2px 4px rgba(15, 23, 42, 0.06), 0 12px 24px rgba(15, 23, 42, 0.12)',
                borderColor: 'rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            <GRTooltip
              hidden={row.dominantMotivations.length === 0 && row.tags.length === 0}
              content={<CreativeAdTooltipContent ad={row} currentMonth={currentMonth} maxInstallShare={maxInstallShare} />}
              tooltipProps={{ enterDelay: 500, enterNextDelay: 200, placement: 'right' }}
              styles={{ display: 'block' }}
            >
              <Box
                aria-label={t('open_creative_ad', { game: row.gameName })}
                {...clickableProps(() => openAd(row.id))}
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  // 9:16 portrait — matches the native aspect ratio of the
                  // vast majority of mobile ad creatives so portrait videos
                  // fill the frame without black side bars. Landscape ads
                  // still letterbox cleanly via `object-fit: contain`.
                  aspectRatio: '9 / 16',
                  backgroundColor: 'rgba(0, 0, 0, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  // Echoes the Blind Spots "reveal" energy: on hover the thumbnail
                  // gently zooms while the play overlay fades up and its button pops.
                  '&:hover .ad-card-play-overlay': { opacity: 0.85 },
                  '&:hover .ad-card-thumb': { transform: 'scale(1.06)' },
                  '&:hover .ad-card-play-button': { transform: 'scale(1.08)' },
                  // Inset: the card above clips its overflow, so an outset ring
                  // would be cut off on the thumbnail's edges.
                  ...focusRingSx(-2),
                }}
              >
                {row.thumbUrl && (
                  <Box
                    component="img"
                    src={row.thumbUrl}
                    alt={row.gameName}
                    loading="lazy"
                    decoding="async"
                    className="ad-card-thumb"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      display: 'block',
                      transformOrigin: 'center',
                      transition: 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    }}
                  />
                )}
                <Box
                  className="ad-card-play-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 200ms ease-in-out',
                    pointerEvents: 'none',
                  }}
                >
                  <Box
                    className="ad-card-play-button"
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transformOrigin: 'center',
                      transition: 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    }}
                  >
                    <PlayArrowRoundedIcon sx={{ color: 'common.white', fontSize: 36 }} />
                  </Box>
                </Box>
                {compareEnabled && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 6,
                      left: 6,
                      backgroundColor: 'rgba(255, 255, 255, 0.85)',
                      borderRadius: '50%',
                      // Backdrop padding so the checkbox stays legible on any
                      // thumbnail without a full white card behind it.
                      p: 0.25,
                    }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Checkbox size="small" checked={isSelected} disabled={checkboxDisabled} onChange={() => onToggleSelection?.(row.id)} sx={{ p: 0.25 }} />
                  </Box>
                )}
              </Box>
            </GRTooltip>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {row.gameIcon && <Box component="img" src={row.gameIcon} alt={row.gameName} loading="lazy" decoding="async" sx={gameIconSx(24)} />}
                <Typography
                  variant="body2"
                  sx={{ ...linkTypographyBoldSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                  {...clickableProps(() => openGame(row.gameId))}
                  title={row.gameName}
                >
                  {row.gameName}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {row.network.name} &middot; {t('seconds', { count: row.videoDurationSeconds })}
              </Typography>
              <RelativeMetricBar pct={performancePct} height={8} labelMinWidth={36} labelAlign="right" sx={{ mt: 0.25 }} />
            </Box>
          </Card>
        )
      })}
    </Box>
  )
}
